import type { GetArAgingReportResponse } from '@vy/api-definition';
import { useMemo } from 'react';
import type { FilterField } from '../../../../../components/new-design/vyTableFilter';
import { useLabels } from '../../../../../hooks/requests/use-labels/use-labels';
import { useSubsidiaries } from '../../../../../hooks/requests/use-subsidiaries';
import {
  buildLabelsFilterField,
  buildLabelsOptions,
  collectUniqueOptions,
  filterNonEmptySelectFields,
  MULTISELECT_OPERATORS,
} from '../../../reports-components/shared-filter-fields';

export const AR_AGING_FILTER_FIELD_IDS = Object.freeze({
  CUSTOMER: 'customerId',
  LABEL: 'labels',
  SUBSIDIARY: 'subsidiaryName',
});

export function useArAgingFilterFields(
  summaryBreakdown: GetArAgingReportResponse['summaryBreakdown'],
): FilterField[] {
  const { data: labelsData } = useLabels({ suspense: false });
  const { data: subsidiariesData } = useSubsidiaries({ suspense: false });

  return useMemo(() => {
    const customerOptions = collectUniqueOptions(
      summaryBreakdown.map((item) => item.customer.name),
    );

    const presentLabelIds = new Set(
      summaryBreakdown.flatMap((item) => item.labels?.map((l) => l.id) ?? []),
    );
    const labelsOptions = buildLabelsOptions(labelsData?.labels, presentLabelIds);

    const subsidiaryOptions = collectUniqueOptions(
      summaryBreakdown.map((item) => item.subsidiaryName ?? null),
    );

    const finalSubsidiaryOptions =
      subsidiaryOptions.length > 0
        ? subsidiaryOptions
        : collectUniqueOptions(
            (subsidiariesData?.subsidiaries ?? []).map((s) => s.name),
          );

    const allFields: FilterField[] = [
      {
        id: AR_AGING_FILTER_FIELD_IDS.CUSTOMER,
        label: 'Customer',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: customerOptions,
      },
      buildLabelsFilterField(labelsOptions),
      {
        id: AR_AGING_FILTER_FIELD_IDS.SUBSIDIARY,
        label: 'Subsidiary',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: finalSubsidiaryOptions,
      },
    ];

    return filterNonEmptySelectFields(allFields);
  }, [summaryBreakdown, labelsData, subsidiariesData]);
}
